import React, { useEffect, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import swal from "sweetalert";
import api from "../../../api";
import UserAvatar from "../Common/UserAvatar";
import PermissionWrapper from "../PermissionWrapper";
import { useSelector } from "react-redux";

/* BACKEND IMAGE URL HELPER */
const getImageUrl = (url) => url || null;

const ClientsList = () => {
 const userDetails = useSelector((state) => state.auth?.userDetails || {});
 const [contacts, setContacts] = useState([]);
 const navigate = useNavigate();
 const [showContactsModal, setShowContactsModal] = useState(false);
 const [selectedClient, setSelectedClient] = useState(null);

 const [currentPage, setCurrentPage] = usePersistentState("clients_page", 1);
 const itemsPerPage = 10;

 useEffect(() => {
 fetchClients();
 }, []);

 const fetchClients = async () => {
 try {
 const res = await api.get("/clients");

 if (res.data.success) {
 const mapped = res.data.data.map((c) => ({
 id: c._id,
 image: c.image,
 title: c.name,
 countryCode: c.countryCode || "+91",
 mobile: c.mobile,
 gmail: c.gmail,
 gst: c.gstDetails?.gstNumber || "—",
 pan: c.gstDetails?.panNumber || "—",
 cin: c.gstDetails?.cinNumber || "—",
 billing: c.billingAddress,
 shipping: c.shippingAddress,
 contactPersons: Array.isArray(c.contactPersons)
 ? c.contactPersons
 : c.contactPerson
 ? [c.contactPerson]
 : [],
 }));

 setContacts(mapped);
 } else {
 swal("Error", "Failed to fetch clients", "error");
 }
 } catch (error) {
 swal("Error", error.message, "error");
 }
 };

 // Pagination logic
 const indexOfLastItem = currentPage * itemsPerPage;
 const indexOfFirstItem = indexOfLastItem - itemsPerPage;
 const currentItems = contacts.slice(indexOfFirstItem, indexOfLastItem);
 const totalPages = Math.ceil(contacts.length / itemsPerPage);

 const handlePageChange = (pageNumber) => {
 setCurrentPage(pageNumber);
 };

 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Are you sure?",
 text: "This client will be deleted permanently.",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const res = await api.delete(`/clients/${id}`);
 if (res.data.success) {
 swal("Deleted!", "Client deleted successfully!", "success");
 fetchClients();
 }
 } catch (error) {
 swal("Error", error.message, "error");
 }
 };
 const closeContactsModal = () => {
 setShowContactsModal(false);
 setSelectedClient(null);
 };

 return (
 <div className="container-fluid">
 {/* HEADER */}
 <div className="d-flex justify-content-between align-items-center mb-4">
 <h3 className="fw-bold">Clients</h3>
 <PermissionWrapper user={userDetails} required={["create:clients"]}>
 <Link to="/clients/add" className="btn btn-primary btn-rounded">
 + Add Client
 </Link>
 </PermissionWrapper>
 </div>

 <div className="row">
 {currentItems.map((c) => (
 <div className="col-xl-4 col-lg-4 col-md-6 mb-4" key={c.id}>
 <div className="card shadow-sm border-0 h-100">
 <div className="card-body">
 {/* TOP */}
 <div className="d-flex align-items-center">
 <UserAvatar
 src={getImageUrl(c.image)}
 size={70}
 alt="client"
 />

 <div className="ms-3">
 <h5 className="mb-1 fw-bold">{c.title}</h5>
 <small className="text-muted">{c.gmail}</small>
 </div>

 <Dropdown className="ms-auto">
 <Dropdown.Toggle
 as="button"
 className="btn btn-link i-false text-dark p-0"
 >
 <i className="fas fa-ellipsis-v fs-5"></i>
 </Dropdown.Toggle>

 <Dropdown.Menu align="end" as={PortalMenu}>
 <Dropdown.Item
 onClick={() => navigate(`/clients/edit/${c.id}`)}
 >
 Edit
 </Dropdown.Item>
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleDelete(c.id)}
 >
 Delete
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </div>

 <hr />

 {/* MOBILE */}
 <p className="mb-1">
 <i className="fas fa-phone-alt me-2 text-primary"></i>
 <strong>{c.countryCode ? `${c.countryCode} ` : ""}{c.mobile}</strong>
 </p>

 {/* TAX */}
 <div className="mt-2">
 <p className="mb-1">
 <strong>GST:</strong> {c.gst}
 </p>
 <p className="mb-1">
 <strong>PAN:</strong> {c.pan}
 </p>
 <p className="mb-1">
 <strong>CIN:</strong> {c.cin}
 </p>
 </div>

 {c.contactPersons.length > 0 && (
 <button
 className="btn btn-outline-primary btn-sm mt-2"
 onClick={() => {
 setSelectedClient(c);
 setShowContactsModal(true);
 }}
 >
 View Contact Persons ({c.contactPersons.length})
 </button>
 )}

 {/* BILLING */}
 <div className="mt-3">
 <h6 className="fw-bold">Billing Address</h6>
 <p className="small text-muted mb-0">
 {c.billing?.addressLine1 || "N/A"}, {c.billing?.city || ""},{" "}
 {c.billing?.state || ""} - {c.billing?.pincode || ""}
 </p>
 </div>

 {/* SHIPPING */}
 <div className="mt-3">
 <h6 className="fw-bold">Shipping Address</h6>
 <p className="small text-muted mb-0">
 {c.shipping?.addressLine1 || "N/A"},{" "}
 {c.shipping?.city || ""}, {c.shipping?.state || ""} -{" "}
 {c.shipping?.pincode || ""}
 </p>
 </div>
 </div>
 </div>
 </div>
 ))}
 </div>

 {/* PAGINATION */}
 <AppPagination
 currentPage={currentPage}
 totalPages={totalPages}
 totalCount={contacts.length}
 itemsLength={10}
 limit={itemsPerPage}
 onPageChange={handlePageChange}
 />

 {/* EMPTY STATE */}
 {contacts.length === 0 && (
 <div className="text-center py-5">
 <h5 className="text-muted">No clients found</h5>
 <Link to="/clients/add" className="btn btn-primary mt-3">
 + Add First Client
 </Link>
 </div>
 )}
 {/* CONTACT PERSONS MODAL */}
 {showContactsModal && selectedClient && (
 <div
 className="modal fade show d-block"
 tabIndex="-1"
 style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
 >
 <div className="modal-dialog modal-lg modal-dialog-centered">
 <div className="modal-content">
 <div className="modal-header">
 <h5 className="modal-title">
 Contact Persons – {selectedClient.title}
 </h5>
 <button
 type="button"
 className="btn-close"
 onClick={closeContactsModal}
 ></button>
 </div>

 <div className="modal-body">
 {selectedClient.contactPersons.length === 0 ? (
 <p className="text-muted">No contact persons added.</p>
 ) : (
 selectedClient.contactPersons.map((person, index) => (
 <div key={index} className="border rounded p-3 mb-3">
 <h6 className="fw-bold mb-2">Contact #{index + 1}</h6>

 {person.name && (
 <p className="mb-1">
 <strong>Name:</strong> {person.name}
 </p>
 )}
 {person.designation && (
 <p className="mb-1">
 <strong>Designation:</strong> {person.designation}
 </p>
 )}
 {person.mobile && (
 <p className="mb-1">
 <strong>Mobile:</strong> {person.countryCode ? `${person.countryCode} ` : ""}{person.mobile}
 </p>
 )}
 {person.email && (
 <p className="mb-1">
 <strong>Email:</strong> {person.email}
 </p>
 )}
 </div>
 ))
 )}
 </div>

 <div className="modal-footer">
 <button
 className="btn btn-secondary"
 onClick={closeContactsModal}
 >
 Close
 </button>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default ClientsList;
